/* eslint-disable no-nested-ternary */
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { capturePHEvents } from '../analytics/posthog.analytics';
import { PH_NEW_ATTENDANCE_CREATED } from '../utils/posthog.eventNames';
import { createAttendance, updateAttendance } from '../service/attendance.service';
import { fetchStudentByClass } from '../service/student.service';
import CheckFillIcon from '../svg/CheckFillIcon';
import palette from '../theme/palette';
import Modal from './common/Modal';

export default function AttendanceModal({
  isOpen,
  handleClose,
  classDetails,
  editAttendance,
  addOrUpdateHandle
}) {
  // Current day slots
  const [todaySlots, setTodaySlots] = useState([]);
  // Selected slot
  const [selectedTime, setSelectedTime] = useState(null);
  // Student list
  const [studentList, setStudentList] = useState([]);
  // Time selection error
  const [selectedTimeError, setSelectedTimeError] = useState('');
  // When infinite scroll data is loading
  const [isDataLoading, setIsDataLoading] = useState(true);
  // Selected org
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  // Current user
  const currentUser = useSelector((state) => state.auth.currentUser);
  // Notification
  const { enqueueSnackbar } = useSnackbar();
  // Loading button state
  const [loadingButton, setButton] = useState(false);

  useEffect(() => {
    // Fetch student list when class is displayed
    const fetchStudents = async () => {
      const studentRes = await fetchStudentByClass(classDetails.id);
      if (studentRes) {
        const newList = studentRes.map((rest) => ({ isPresent: false, ...rest.userId }));

        if (editAttendance) {
          editAttendance.presentStudents.forEach((ps) => {
            const findIndex = newList.findIndex((sl) => sl?._id === ps);
            if (findIndex > -1) {
              newList[findIndex].isPresent = true;
            }
          });
          setStudentList(newList);
        } else {
          setStudentList(newList);
        }
        setIsDataLoading(false);
      } else {
        setStudentList([]);
        setIsDataLoading(false);
      }
    };

    // Fetch student list for current class
    fetchStudents();
  }, []);

  /**
   * When modal is open then will check with current time
   * If current time is found in slots then assign it to selected slot
   * Otherwise set the first element current slot.
   */
  useEffect(() => {
    if (isOpen) {
      // Get the current day from moment
      // As the days are stored from monday to sunday.
      // So moment returns 0 incase its Sunday and in our array its in 6th position that's why manually we've to do it.
      const todayDayName = moment().day() === 0 ? 6 : moment().day() - 1;
      let currentSlot = null;
      const timeSlots = classDetails.schedule[todayDayName];
      // Loop through the slots
      timeSlots.slots.forEach((slot, index) => {
        const classBeginningTime = moment(slot.from);
        const classEndTime = moment(slot.to);
        const currentTime = moment(new Date());

        // Check the current time with slot timing is the current one is in between then apply to selected slot.
        if (currentTime.isAfter(classBeginningTime) && currentTime.isBefore(classEndTime)) {
          currentSlot = timeSlots.slots[index];
        }
      });
      setSelectedTime(currentSlot || (timeSlots?.slots.length ? timeSlots?.slots[0] : []));
      setTodaySlots(timeSlots.slots);
    }
  }, [isOpen]);

  /**
   * When we have edit attendance object
   */
  useEffect(() => {
    if (editAttendance && todaySlots.length > 0) {
      const selectedSlot = todaySlots.find((ts) => ts._id === editAttendance.slotId) || null;
      if (selectedSlot) setSelectedTime(selectedSlot);
    }
  }, [editAttendance, todaySlots]);

  /**
   * This function is used for toggle attendance
   *
   * @param {number} studIndex index from array.
   * @returns {array} updated student array.
   */
  function toggleAttendance(studIndex) {
    const updatedList = [...studentList];
    updatedList[studIndex].isPresent = !updatedList[studIndex].isPresent;
    setStudentList(updatedList);
  }

  /**
   * Before calling the api to save need to require field.
   * @returns {boolean} is all field are valid or not
   */
  function validationCheck() {
    let allValid = true;

    if (!selectedTime) {
      setSelectedTimeError('Please select time of the class.');
      allValid = false;
    }
    return allValid;
  }

  /**
   * This function is used to make api call.
   */
  async function markAttendance() {
    if (validationCheck()) {
      setButton(true);
      if (editAttendance) {
        const attendanceObj = {
          presentStudents: studentList.filter((sl) => sl.isPresent).map((sl) => sl._id ?? sl?.id),
          absentStudents: studentList.filter((sl) => !sl.isPresent).map((sl) => sl?._id ?? sl?.id)
        };

        const res = await updateAttendance(classDetails.id, editAttendance.id, attendanceObj);
        if (res) {
          enqueueSnackbar('Attendance Updated', {
            variant: 'success'
          });
          addOrUpdateHandle();
          closeDialog();
          setButton(false);
        } else {
          enqueueSnackbar('Something went wrong please try again', {
            variant: 'error'
          });
          setButton(false);
        }
      } else {
        const attendanceObj = {
          orgId: selectedOrg?.orgId || selectedOrg?.id,
          type: 'offline',
          slotId: selectedTime._id,
          slot: `slot${todaySlots.findIndex((ts) => ts._id === selectedTime._id) + 1}`,
          slotTimeFrom: selectedTime.from,
          slotTimeTo: selectedTime.to,
          presentStudents: studentList.filter((sl) => sl.isPresent).map((sl) => sl._id ?? sl?.id),
          absentStudents: studentList.filter((sl) => !sl.isPresent).map((sl) => sl?._id ?? sl?.id),
          attendanceTaker: currentUser.id
        };
        const res = await createAttendance(classDetails.id, attendanceObj);
        if (res) {
          enqueueSnackbar('Attendance Added', {
            variant: 'success'
          });
          capturePHEvents(PH_NEW_ATTENDANCE_CREATED, {
            attendanceId: res.id,
            orgId: attendanceObj.orgId,
            classId: classDetails.id
          });
          addOrUpdateHandle();
          closeDialog();
          setButton(false);
        } else {
          enqueueSnackbar('Something went wrong please try again', {
            variant: 'error'
          });
          setButton(false);
        }
      }
    }
  }

  /**
   * Reset all variable to init state and close dialog
   */
  function closeDialog() {
    setSelectedTime(null);
    // Taking studentList as instance of array in new variable
    const updatedList = [...studentList];
    const newList = updatedList.map((rest) => ({ ...rest, isPresent: false }));
    setStudentList(newList);
    handleClose();
  }

  // if (isMobile) {
  //   return (
  //     <MobileModal isOpen={isOpen} onClose={() => closeDialog()} headerTitle="Attendance">
  //       <DialogContent id="scrollableCard">
  //         {!isDataLoading && (
  //           <FormControl fullWidth>
  //             <InputLabel id="demo-multiple-name-label">Select time slot</InputLabel>
  //             {/* Select class timing */}
  //             <Select
  //               labelId="demo-multiple-name-label"
  //               id="demo-multiple-name"
  //               value={selectedTime}
  //               label="Select time slot"
  //               disabled={editAttendance}
  //               onChange={(v) => {
  //                 setSelectedTime(v.target.value);
  //                 setSelectedTimeError('');
  //               }}
  //               error={selectedTimeError}
  //             >
  //               {todaySlots.length === 0 && <MenuItem disabled>No slot for today</MenuItem>}
  //               {todaySlots.map((slot, index) => (
  //                 <MenuItem key={index} value={slot}>
  //                   {moment(slot.from).format('LT')} to {moment(slot.to).format('LT')}
  //                 </MenuItem>
  //               ))}
  //             </Select>
  //             {selectedTimeError && (
  //               <FormHelperText sx={{ color: 'red' }}>{selectedTimeError}</FormHelperText>
  //             )}
  //           </FormControl>
  //         )}
  //         <Box>
  //           {/* Check if student is 0 then show some text */}
  //           {isDataLoading ? (
  //             <Box my={4}>
  //               <Stack direction="row" justifyContent="center">
  //                 <CircularProgress />
  //               </Stack>
  //             </Box>
  //           ) : studentList?.length === 0 ? (
  //             <Box textAlign="center" mt={5} mb={5}>
  //               <Typography>No students found</Typography>
  //               <Typography>
  //                 Add student from <b>Student</b> tab by clicking <b>+ Add Student</b> button.
  //               </Typography>
  //             </Box>
  //           ) : (
  //             <Box mt={2}>
  //               <Typography mb={2} alignItems="center" display="flex">
  //                 <b>Note:</b> Mark attendance by clicking on{' '}
  //                 <CheckFillIcon color={palette.grey[400]} style={{ marginLeft: '5px' }} />
  //               </Typography>
  //               {studentList.map((sl, index) => (
  //                 <Stack
  //                   direction="row"
  //                   alignItems="center"
  //                   justifyContent="space-between"
  //                   key={sl.id}
  //                   mb={2}
  //                 >
  //                   <Stack direction="row" alignItems="center" spacing={1}>
  //                     <Avatar alt={sl.fullName} src={sl.profileImage} sx={{ mr: 2 }} />
  //                     <Typography variant="subtitle2" noWrap>
  //                       {sl.fullName}
  //                     </Typography>
  //                   </Stack>
  //                   <IconButton style={{ marginLeft: 5 }} onClick={() => toggleAttendance(index)}>
  //                     <CheckFillIcon
  //                       color={sl.isPresent ? palette.secondary.main : palette.grey[400]}
  //                     />
  //                   </IconButton>
  //                 </Stack>
  //               ))}
  //             </Box>
  //           )}
  //         </Box>
  //       </DialogContent>
  //       <DialogActions>
  //         <Button disabled={loadingButton} onClick={() => closeDialog()}>
  //           Cancel
  //         </Button>
  //         <LoadingButton
  //           loading={loadingButton}
  //           variant="contained"
  //           onClick={() => markAttendance()}
  //           disabled={studentList.length === 0}
  //         >
  //           {editAttendance ? 'Update' : 'Add'}
  //         </LoadingButton>
  //       </DialogActions>
  //     </MobileModal>
  //   );
  // }

  return (
    <Modal
      open={isOpen}
      onClose={() => closeDialog()}
      cancelText="Cancel"
      actionText={editAttendance ? 'Update' : 'Add'}
      isBtnLoading={loadingButton}
      isCancelDisabled={loadingButton}
      onAction={() => markAttendance()}
      onCancel={() => closeDialog()}
      isActionDisabled={studentList.length === 0}
    >
      {!isDataLoading && (
        <FormControl fullWidth>
          <InputLabel id="demo-multiple-name-label">Select time slot</InputLabel>
          {/* Select class timing */}
          <Select
            labelId="demo-multiple-name-label"
            id="demo-multiple-name"
            value={selectedTime}
            label="Select time slot"
            disabled={editAttendance}
            onChange={(v) => {
              setSelectedTime(v.target.value);
              setSelectedTimeError('');
            }}
            error={selectedTimeError}
          >
            {todaySlots.length === 0 && <MenuItem disabled>No slot for today</MenuItem>}
            {todaySlots.map((slot, index) => (
              <MenuItem key={index} value={slot}>
                {moment(slot.from).format('LT')} to {moment(slot.to).format('LT')}
              </MenuItem>
            ))}
          </Select>
          {selectedTimeError && (
            <FormHelperText sx={{ color: 'red' }}>{selectedTimeError}</FormHelperText>
          )}
        </FormControl>
      )}
      <Box>
        {/* Check if student is 0 then show some text */}
        {isDataLoading ? (
          <Box my={4}>
            <Stack direction="row" justifyContent="center">
              <CircularProgress />
            </Stack>
          </Box>
        ) : studentList?.length === 0 ? (
          <Box textAlign="center" mt={5} mb={5}>
            <Typography>No students found</Typography>
            <Typography>
              Add student from <b>Student</b> tab by clicking <b>+ Add Student</b> button.
            </Typography>
          </Box>
        ) : (
          <Box mt={2}>
            <Typography mb={2} alignItems="center" display="flex">
              <b>Note:</b> Mark attendance by clicking on{' '}
              <CheckFillIcon color={palette.grey[400]} style={{ marginLeft: '5px' }} />
            </Typography>
            {studentList.map((sl, index) => (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                key={sl.id}
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar alt={sl.fullName} src={sl.profileImage} sx={{ mr: 2 }} />
                  <Typography variant="subtitle2" noWrap>
                    {sl.fullName}
                  </Typography>
                </Stack>
                <IconButton style={{ marginLeft: 5 }} onClick={() => toggleAttendance(index)}>
                  <CheckFillIcon
                    color={sl.isPresent ? palette.secondary.main : palette.grey[400]}
                  />
                </IconButton>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
    </Modal>
    // <Dialog fullWidth open={isOpen} onClose={() => closeDialog()}>
    //   <DialogContent id="scrollableCard">

    //   </DialogContent>
    //   <DialogActions>
    //     <Button disabled={loadingButton} onClick={() => closeDialog()}>
    //       Cancel
    //     </Button>
    //     <LoadingButton
    //       loading={loadingButton}
    //       variant="contained"
    //       onClick={() => c()}
    //       disabled={studentList.length === 0}
    //     >
    //       {editAttendance ? 'Update' : 'Add'}
    //     </LoadingButton>
    //   </DialogActions>
    // </Dialog>
  );
}

AttendanceModal.propTypes = {
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  classDetails: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    schedule: PropTypes.array
  }),
  editAttendance: PropTypes.shape({
    id: PropTypes.string,
    slotId: PropTypes.string,
    presentStudents: PropTypes.array
  }),
  addOrUpdateHandle: PropTypes.func
};
